import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { ORG_SCHEMA } from "../data/seo";
import { SERVICES, APPROACHES, TESTIMONIALS, MANIFESTO, IMAGES, CONTACT, RETREAT, INSURANCE_ACCEPTED, FEES } from "../data/site";

const Marquee = () => {
  const words = ["Faith", "Healing", "Hope", "Restoring Hearts", "Renewing Minds", "Be Renewed"];
  const row = [...words, ...words, ...words];
  return (
    <div className="py-10 border-y border-gold/20 overflow-hidden bg-cream" data-testid="home-marquee">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {row.map((w, i) => (
          <span key={i} className="font-serif italic text-4xl md:text-6xl text-navy/15 mx-8 flex items-center gap-8">
            {w} <span className="text-gold/40 not-italic">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  const teaserRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: teaserRef, offset: ["start end", "end start"] });
  const oceanY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Christian Counseling in Wesley Chapel, FL | Beyond Blessed"
        description="Faith-based therapy in Wesley Chapel, Tampa & online Christian counseling statewide. Trauma, couples, groups & retreats. Book a free 15-min consultation."
        path="/"
        jsonLd={ORG_SCHEMA}
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-cream via-seafoam/25 to-cream" />
          <img src={RETREAT ? IMAGES.retreatHero : ""} alt="Peaceful beach shoreline at sunrise" className="w-full h-full object-cover opacity-30" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}
            className="font-sans uppercase tracking-[0.4em] text-xs text-teal mb-6"
          >
            Wesley Chapel · Tampa · Telehealth Statewide
          </motion.p>

          <h1 className="font-serif font-light text-navy text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            <MaskedLines lines={["Restoring Hearts.", "Renewing Minds."]} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
            className="mt-4 font-script text-4xl md:text-5xl text-gold"
          >
            Reviving God's purpose for your life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 1 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <a href={CONTACT.request} target="_blank" rel="noreferrer" data-testid="hero-request-appointment"
              className="group bg-navy text-cream px-8 py-4 rounded-full flex items-center gap-2 hover:bg-teal transition-colors duration-300">
              Book Free Consultation <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
            <Link to="/retreats" data-testid="hero-retreat-link" className="border border-gold text-navy px-8 py-4 rounded-full hover:bg-gold hover:text-cream transition-colors duration-300">
              Explore the Retreat
            </Link>
            <div className="flex items-center gap-2 sm:ml-4">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-gold text-gold" />)}
              <span className="text-navy/60 text-sm">5.0 rated on Grow Therapy</span>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
            className="mt-4 text-navy/60 text-sm italic flex items-center gap-2"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" /> Free 15-minute consultation — no commitment
          </motion.p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28" data-testid="home-manifesto">
        <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.12}>
              <div className="border-t border-gold/30 pt-6">
                <span className="font-serif text-gold text-2xl">{m.n}</span>
                <h3 className="font-serif text-3xl text-navy mt-4 mb-3">{m.title}</h3>
                <p className="text-charcoal/70 leading-relaxed">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT split */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-16 items-center" data-testid="home-about">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-6">About</p>
          <h2 className="font-serif text-4xl md:text-5xl text-navy leading-tight mb-6">Be seen, be heard,<br /> grow &amp; transform.</h2>
          <p className="text-charcoal/75 leading-relaxed mb-5">
            Feeling overwhelmed, stuck, or weighed down by past experiences? You're not alone—and you don't have to
            navigate this on your own. We help you find relief, clarity, and hope through care rooted in faith,
            compassion, and clinical excellence.
          </p>
          <p className="text-charcoal/75 leading-relaxed mb-8">
            Our approach is client-centered and integrative—CBT, Schema Therapy, Somatic Experiencing, Mindfulness—with
            Christian counseling available upon request.
          </p>
          <Link to="/team" className="link-underline text-navy font-medium inline-flex items-center gap-2" data-testid="about-team-link">
            Meet your guides <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="bg-white p-3 shadow-sm rotate-[-2deg]">
              <div className="w-full h-[520px] overflow-hidden">
                <img src={IMAGES.tanya} alt="Tanya Richardson" className="w-full h-full object-cover" style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
              </div>
              <p className="text-center font-serif text-navy text-lg py-3">Tanya Richardson, LMHC</p>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-teal text-cream px-6 py-4 hidden sm:block">
              <p className="font-serif text-2xl">Your personal</p>
              <p className="font-script text-3xl text-goldbright leading-none">psychotherapist</p>
            </div>
          </div>
        </Reveal>
      </section>

      <Marquee />

      {/* SERVICES bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28" data-testid="home-services">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
            <h2 className="font-serif text-4xl md:text-5xl text-navy">How we help you heal</h2>
            <p className="text-charcoal/60 max-w-sm">Evidence-based, faith-integrated care for individuals, couples, and communities.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06} className={s.span ? "md:col-span-2" : ""}>
              <div className={`h-full p-10 border border-gold/25 transition-all duration-500 hover:border-teal group ${s.span ? "bg-navy text-cream" : "bg-white"}`}>
                <span className={`font-serif text-sm ${s.span ? "text-goldbright" : "text-gold"}`}>0{i + 1}</span>
                <h3 className={`font-serif text-2xl md:text-3xl mt-4 mb-3 ${s.span ? "text-cream" : "text-navy"}`}>{s.title}</h3>
                <p className={s.span ? "text-cream/70" : "text-charcoal/70"}>{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap gap-3">
            {APPROACHES.map((a) => (
              <span key={a} className="text-sm border border-navy/15 rounded-full px-4 py-2 text-navy/70 hover:border-gold transition-colors">{a}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* RETREAT TEASER parallax */}
      <section ref={teaserRef} className="relative h-[80vh] overflow-hidden flex items-center" data-testid="home-retreat-teaser">
        <motion.img src={IMAGES.ocean} alt="Ocean waves rolling at dusk" style={{ y: oceanY }} className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-navy/55" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full text-cream">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-xs text-goldbright mb-4">Beyond Blessed Presents</p>
            <p className="font-script text-5xl md:text-7xl text-goldbright leading-none mb-3">Renewed &amp; Restored</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-6 max-w-2xl">A Christian Women's Therapy Intensive Retreat</h2>
            <p className="text-cream/80 max-w-xl mb-8">{RETREAT.dates} · {RETREAT.location}. Heal. Grow. Connect. Be Renewed.</p>
            <Link to="/retreats" data-testid="teaser-retreat-cta" className="bg-goldbright text-navy px-8 py-4 rounded-full inline-flex items-center gap-2 hover:bg-cream transition-colors duration-300 font-medium">
              Reserve your place <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-28 text-center" data-testid="home-testimonials">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-10">Kind Words</p>
          <div className="space-y-16">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <blockquote className="font-serif text-2xl md:text-3xl text-navy leading-relaxed italic">"{t.quote}"</blockquote>
                <cite className="block mt-5 not-italic text-charcoal/50 text-sm tracking-wide">— {t.who}</cite>
              </Reveal>
            ))}
          </div>
          <p data-testid="testimonials-attribution" className="mt-14 text-xs uppercase tracking-widest text-charcoal/50">
            Verified client reviews shared via{" "}
            <a href={CONTACT.growTherapy} target="_blank" rel="noreferrer" className="underline hover:text-teal transition-colors">Grow Therapy</a>
          </p>
        </Reveal>
      </section>

      {/* INSURANCE & FEES */}
      <section className="bg-cream py-24" data-testid="home-insurance-fees">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">Coverage</p>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6 leading-tight">Insurance we accept</h2>
            <p className="text-charcoal/70 mb-8 max-w-md">We're in-network with major insurance providers and also offer self-pay options for greater privacy and flexibility.</p>
            <div className="flex flex-wrap gap-3">
              {INSURANCE_ACCEPTED.map((i) => (
                <span key={i} className="border border-teal/40 text-navy px-5 py-2.5 text-sm rounded-full bg-white">{i}</span>
              ))}
            </div>
            <p className="text-charcoal/50 text-xs mt-8 italic">Not sure if your plan is covered? Reach out and we'll verify your benefits.</p>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">Fees</p>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mb-8 leading-tight">Transparent pricing</h2>
            <div className="divide-y divide-gold/25 border-y border-gold/25">
              {FEES.map((f) => (
                <div key={f.label} className="py-5 flex items-baseline justify-between gap-4">
                  <div>
                    <p className="font-serif text-xl text-navy">{f.label}</p>
                    <p className="text-charcoal/55 text-sm">{f.note}</p>
                  </div>
                  <p className="font-serif text-2xl text-teal shrink-0">{f.price}</p>
                </div>
              ))}
            </div>
            <p className="text-charcoal/50 text-xs mt-6 italic">Sliding scale considered for self-pay clients when available.</p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-teal text-cream py-24" data-testid="home-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="font-script text-4xl md:text-5xl text-goldbright mb-3">Not sure where to start?</p>
            <h2 className="font-serif text-4xl md:text-6xl mb-6">Book a <span className="italic">free</span> 15-minute consultation</h2>
            <p className="text-cream/80 mb-10 max-w-xl mx-auto">No pressure, no commitment—just a warm conversation to see if we're the right fit for your healing journey.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href={CONTACT.request} target="_blank" rel="noreferrer" data-testid="cta-request" className="bg-cream text-navy px-8 py-4 rounded-full hover:bg-goldbright transition-colors font-medium">Book Free Consultation</a>
              <Link to="/contact" data-testid="cta-contact" className="border border-cream/40 px-8 py-4 rounded-full hover:bg-cream/10 transition-colors">Send a Message</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
